// AGC: Quick Add Menu - Floating Action Button menu for lot notes
// AGC: Uses React Portal to render at document root (breaks out of stacking context)
// AGC: Positioned BELOW button to avoid overflow with top menu
// AGC: Dark overlay on entire page when open

import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { Camera, Pencil, Mic, X, Plus, Video } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'

interface QuickAddMenuProps {
  onPhotoCapture: () => void
  onVideoCapture: () => void
  onAudioNote: () => void
  onTextNote: () => void
}

/**
 * AGC: QuickAddMenu - FAB with expandable menu for creating notes
 */
export const QuickAddMenu: React.FC<QuickAddMenuProps> = ({
  onPhotoCapture,
  onVideoCapture,
  onAudioNote,
  onTextNote,
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)

  const toggleMenu = () => {
    if (!isOpen && buttonRef.current) {
      // AGC: Capture button position when opening
      setButtonRect(buttonRef.current.getBoundingClientRect())
    }
    setIsOpen(!isOpen)
  }

  const handleAction = (action: () => void) => {
    action()
    setIsOpen(false) // AGC: Close menu after selection
  }

  // AGC: Portal content (rendered at document root to escape stacking context)
  const portalContent = isOpen && buttonRect && (
    <>
      {/* AGC: FULL PAGE Dark Overlay - HIGHEST z-index to be on top of EVERYTHING */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99998] animate-fade-in"
        onClick={() => setIsOpen(false)}
      />

      {/* AGC: Menu items - positioned absolutely using button's coordinates */}
      <div 
        className="fixed z-[99999] flex flex-col gap-3"
        style={{
          top: `${buttonRect.bottom + 16}px`, // AGC: 16px below button
          right: `${window.innerWidth - buttonRect.right}px`, // AGC: Align with button's right edge
        }}
      >
        {/* AGC: Photo/Camera option - appears FIRST (0ms delay) */}
        <button
          onClick={() => handleAction(onPhotoCapture)}
          className="flex items-center gap-3 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-full shadow-xl pl-4 pr-5 py-3 transition-all transform hover:scale-105"
          style={{
            animation: 'slideInRight 0.3s ease-out 0ms forwards',
            opacity: 0,
            transform: 'translateX(20px) scale(0.8)',
          }}
        >
          <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
            <Camera className="w-5 h-5 text-white" />
          </div>
          <span className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
            {t('notes.takePhoto')}
          </span>
        </button>

        {/* AGC: Video option - appears SECOND (80ms delay) */}
        <button
          onClick={() => handleAction(onVideoCapture)}
          className="flex items-center gap-3 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-full shadow-xl pl-4 pr-5 py-3 transition-all transform hover:scale-105"
          style={{
            animation: 'slideInRight 0.3s ease-out 80ms forwards',
            opacity: 0,
            transform: 'translateX(20px) scale(0.8)',
          }}
        >
          <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
            <Video className="w-5 h-5 text-white" />
          </div>
          <span className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
            {t('notes.recordVideo')}
          </span>
        </button>

        {/* AGC: Text note option - appears THIRD (160ms delay) */}
        <button
          onClick={() => handleAction(onTextNote)}
          className="flex items-center gap-3 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-full shadow-xl pl-4 pr-5 py-3 transition-all transform hover:scale-105"
          style={{
            animation: 'slideInRight 0.3s ease-out 160ms forwards',
            opacity: 0,
            transform: 'translateX(20px) scale(0.8)',
          }}
        >
          <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
            <Pencil className="w-5 h-5 text-white" />
          </div>
          <span className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
            {t('notes.writeNote')}
          </span>
        </button>

        {/* AGC: Audio note option - appears FOURTH (240ms delay) */}
        <button
          onClick={() => handleAction(onAudioNote)}
          className="flex items-center gap-3 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-full shadow-xl pl-4 pr-5 py-3 transition-all transform hover:scale-105"
          style={{
            animation: 'slideInRight 0.3s ease-out 240ms forwards',
            opacity: 0,
            transform: 'translateX(20px) scale(0.8)',
          }}
        >
          <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
            <Mic className="w-5 h-5 text-white" />
          </div>
          <span className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
            {t('notes.recordAudio')}
          </span>
        </button>
      </div>

      {/* AGC: Inline CSS for Apple-style slide-in animation */}
      <style>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px) scale(0.8);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
        
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.2s ease-out forwards;
        }
      `}</style>
    </>
  )

  return (
    <>
      {/* AGC: Portal renders overlay + menu at document root (escapes all stacking contexts) */}
      {typeof document !== 'undefined' && createPortal(portalContent, document.body)}

      {/* AGC: Main FAB button (stays in original position) */}
      <button
        ref={buttonRef}
        onClick={toggleMenu}
        className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all transform relative ${
          isOpen
            ? 'bg-gray-700 dark:bg-gray-600 rotate-45 scale-110'
            : 'bg-emerald-600 hover:bg-emerald-700 hover:scale-110'
        }`}
        aria-label={isOpen ? t('common.close') : t('notes.quickAdd')}
      >
        {isOpen ? (
          <X className="w-5 h-5 text-white" />
        ) : (
          <Plus className="w-5 h-5 text-white" />
        )}
      </button>
    </>
  )
}

export default QuickAddMenu
